import { Component } from '@theme/component';

/** Finite, manually controlled gallery. Native overflow remains usable without JavaScript. */
class TampinoGallery extends Component {
  requiredRefs = ['track', 'previous', 'next', 'status'];

  connectedCallback() {
    super.connectedCallback();
    this.connectControls();
  }

  updatedCallback() {
    super.updatedCallback();
    this.connectControls();
  }

  connectControls() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.controller = new AbortController();
    const { track } = this.refs;
    const options = { passive: true, signal: this.controller.signal };
    track.addEventListener('scroll', () => this.updateControls(), options);
    // A manual gesture takes over from an unfinished arrow-button scroll.
    for (const event of ['pointerdown', 'wheel', 'keydown']) {
      track.addEventListener(event, () => { this.destination = undefined; }, options);
    }
    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.refs.track);
    this.updateControls();
    this.setAttribute('data-enhanced', '');
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  updateControls() {
    const { track, previous, next, status, counter } = this.refs;
    const { slides, offsets, lastStart, visible } = this.getMetrics();
    if (!slides.length) return;
    const current = offsets.slice(0, lastStart + 1).reduce((best, offset, index) =>
      Math.abs(offset - track.scrollLeft) < Math.abs(offsets[best] - track.scrollLeft) ? index : best, 0);
    this.current = current;
    previous.disabled = current === 0;
    next.disabled = current === lastStart;
    if (this.destination != null && Math.abs(track.scrollLeft - offsets[this.destination]) < 2) {
      this.destination = undefined;
    }
    const first = current + 1;
    const last = Math.min(slides.length, current + visible);
    const range = this.hasAttribute('data-show-range') && last > first;
    const position = range ? `${first}–${last}` : `${first}`;
    if (counter) counter.textContent = `${position} / ${slides.length}`;
    const template = this.getAttribute(range ? 'data-status-range' : 'data-status-single');
    const label = template
      ? template.replace('[number]', String(first)).replace('[first]', String(first)).replace('[last]', String(last))
      : `${first} / ${slides.length}`;
    if (status.textContent !== label) status.textContent = label;
    for (const [index, link] of (this.refs.positions ?? []).entries()) {
      const wasFocused = link === document.activeElement;
      link.hidden = index > lastStart;
      if (index === current) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
      if (link.hidden && wasFocused) {
        this.refs.positions[current]?.focus({ preventScroll: true });
      }
    }
  }

  previous() { this.move(-1); }
  next() { this.move(1); }

  /** Direct position links keep native anchors when enhancement is unavailable. */
  select(event) {
    const link = event.target instanceof Element ? event.target.closest('[data-gallery-index]') : null;
    if (!link || !this.contains(link)) return;
    event.preventDefault();
    this.goTo(Number(link.dataset.galleryIndex));
  }

  move(direction) {
    this.goTo((this.destination ?? this.current ?? 0) + direction);
  }

  goTo(index) {
    if (!Number.isFinite(index)) return;
    const track = this.refs.track;
    const { offsets, lastStart } = this.getMetrics();
    if (!offsets.length) return;
    this.destination = Math.max(0, Math.min(lastStart, Math.round(index)));
    track.scrollTo({ left: offsets[this.destination], behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    this.updateControls();
  }

  /** Derive finite start positions from the real rail, including multi-card views. */
  getMetrics() {
    const track = this.refs.track;
    const slides = Array.from(track.children);
    const left = track.getBoundingClientRect().left;
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    const offsets = slides.map(slide => slide.getBoundingClientRect().left - left + track.scrollLeft);
    const lastStart = offsets.reduce((last, offset, index) => offset <= max + 2 ? index : last, 0);
    const width = slides[0]?.getBoundingClientRect().width ?? track.clientWidth;
    const visible = width > 0 ? Math.max(1, Math.round(track.clientWidth / width)) : 1;
    return { slides, offsets, lastStart, visible };
  }
}

if (!customElements.get('tampino-gallery-component')) customElements.define('tampino-gallery-component', TampinoGallery);
